import { ApiService } from "@/services/api/ApiService";
import { Agent } from "@/types/agent/agent";
import { AgentCacheManager } from "./AgentCacheManager";

export interface GenieLimitsScope {
  currentCount: number;
  maxGenies: number;
  isAtLimit: boolean;
}

export interface GenieLimits {
  personal: GenieLimitsScope;
  team: GenieLimitsScope | null;
}

export interface AgentFilters {
  searchTerm?: string;
  role?: string;
  category?: string;
  llmModel?: string;
  voiceLabel?: string;
  isActive?: boolean;
  isPublic?: boolean;
  isDemoGenie?: boolean;
  isMarketplaceGenie?: boolean;
  dailyCallLimit?: number;
  reportMode?: string;
  groupId?: string | number | "ungrouped";
}

export class AgentQueryService {
  public static async getAgents(cursor?: string, limit: number = 30, isAdmin: boolean = false, userId?: string, filters?: AgentFilters) {
    try {
      const response = await ApiService.invoke({
        resource: "genies",
        action: "all",
        data: {
          cursor,
          limit,
          adminMode: isAdmin,
          userId,
          ...filters,
        },
      });

      return {
        data: response.data,
        nextCursor: response.nextCursor,
        hasMore: response.hasMore,
        total: response.total,
      };
    } catch (error: any) {
      console.error("Error fetching agents:", error);
      throw new Error(error.message || "Failed to get agents");
    }
  }

  public static async getAgentDetails(id: number | string): Promise<Agent> {
    try {
      const response = await ApiService.invoke({
        resource: "genies",
        action: "get",
        id: String(id),
        data: { adminMode: true },
      });

      console.log("Fetched fresh details data: ", response);

      return response.agent;
    } catch (error: any) {
      console.error("Error fetching agent details:", error);
      throw new Error(error.message || "Failed to get agents");
    }
  }

  /**
   * Self-heals a genie's signatureStyle.phoneNumber/liveUrl from its live
   * phone number and url_name (agents/signature-style handler). Only those
   * two fields are ever touched server-side — every other signatureStyle
   * preference is left exactly as saved.
   */
  public static async fixSignatureContacts(id: string): Promise<any> {
    try {
      const response = await ApiService.invoke({
        resource: "genies",
        action: "signature-style",
        id,
      });

      return response.branding;
    } catch (error: any) {
      console.error("Error refreshing signature contact info:", error);
      throw new Error(error.message || "Failed to refresh signature contact info");
    }
  }

  public static async list(searchName?: string, userId?: string) {
    try {
      const response = await ApiService.invoke({
        resource: "genies",
        action: "list",
        data: {
          userId,
          adminMode: !!userId,
          limit: 500,
          filters: searchName ? { selectedUsername: searchName } : undefined,
        },
      });

      return response.agents || [];
    } catch (error: any) {
      console.error("Error fetching agents list:", error);
      throw new Error(error.message || "Failed to get agents");
    }
  }

  public static async getLimits(): Promise<GenieLimits> {
    try {
      const response = await ApiService.invoke({
        resource: "genies",
        action: "limits",
      });

      return {
        personal: response.personal,
        team: response.team,
      };
    } catch (error: any) {
      console.error("Error fetching genie limits:", error);
      throw new Error(error.message || "Failed to get genie limits");
    }
  }

  public static async listMarketplaceGenies(limit: number = 500) {
    try {
      const response = await ApiService.invoke({
        resource: "genies",
        action: "list",
        data: {
          onlyMarketplace: true,
          adminMode: true,
          limit,
        },
      });

      return response.agents || [];
    } catch (error: any) {
      console.error("Error fetching marketplace genies:", error);
      throw new Error(error.message || "Failed to get marketplace genies");
    }
  }

  public static async reorder(sort: Record<string, number>): Promise<void> {
    try {
      const data = await ApiService.invoke({
        resource: "genies",
        action: "reorder",
        data: { sort },
      });

      if (!data?.success) {
        throw new Error(data?.message || "Failed to reorder agents");
      }

      AgentCacheManager.invalidateCache();
    } catch (error) {
      console.error("Error reordering agents:", error);
      throw new Error(error instanceof Error ? error.message : "Failed to reorder agents");
    }
  }
}
